import {
  Space,
  Typography,
} from 'antd'
import React, { useMemo } from 'react'

import { StatisticCreator } from '@/model/analytics'
import {
  formatNAText,
  formatPercentage,
  formatPrice,
} from '@/utils/formatter'

const { Text } = Typography

function TextWithGrowth({ value, growth }: {
  value: string,
  growth: number | null
}) {
  const g = growth || 0

  return (<Space>
    <Text>{value}</Text>
    <Text style={{ fontSize: 12 }}
      strong
      type={(g || 0) > 0 ? 'success' : 'danger'}>{formatPercentage()(growth)}</Text>
  </Space>)
}

export default function useStatisticColumns() {
  const columns = useMemo(() => [
    {
      title: 'Creators',
      dataIndex: 'name',
      key: 'name',
      sorter: (a: StatisticCreator, b: StatisticCreator) => a.name.localeCompare(b.name),
      columnChecked: true,
    },
    {
      title: 'Subscriptions',
      dataIndex: 'subscription',
      key: 'subscription',
      sorter: (a: StatisticCreator, b: StatisticCreator) => a.subscription - b.subscription,
      defaultColumnChecked: true,
      render: (value: number, record: StatisticCreator) => {
        return <TextWithGrowth value={`${formatPrice()(value)}`} growth={record.newSubsGrowth} />
      },
    },
    {
      title: 'Tips',
      dataIndex: 'tips',
      key: 'tips',
      sorter: (a: StatisticCreator, b: StatisticCreator) => a.tips - b.tips,
      defaultColumnChecked: true,
      render: (value: number) => formatPrice()(value),
    },
    {
      title: 'Posts',
      dataIndex: 'posts',
      key: 'posts',
      sorter: (a: StatisticCreator, b: StatisticCreator) => a.posts - b.posts,
      defaultColumnChecked: true,
      render: (value: number) => formatPrice()(value),
    },
    {
      title: 'Message',
      dataIndex: 'message',
      key: 'message',
      sorter: (a: StatisticCreator, b: StatisticCreator) => a.message - b.message,
      defaultColumnChecked: true,
      render: (value: number) => formatPrice()(value),
    },
    {
      title: 'Total earnings',
      dataIndex: 'total',
      key: 'total',
      sorter: (a: StatisticCreator, b: StatisticCreator) => a.total - b.total,
      defaultColumnChecked: true,
      render: (value: number, record: StatisticCreator) => {
        return <TextWithGrowth value={`${formatPrice()(value)}`} growth={record.totalGrowth} />
      },
    },
    {
      title: 'Total active fans',
      dataIndex: 'totalActiveFans',
      key: 'totalActiveFans',
      sorter: (a: StatisticCreator, b: StatisticCreator) => a.totalActiveFans - b.totalActiveFans,
      defaultColumnChecked: true,
      render: (value: number, record: StatisticCreator) => {
        return (
          <TextWithGrowth
            value={`${formatNAText()(value)}`}
            growth={record.totalActiveFansGrowth}
          />
        )
      },
    },
    {
      title: 'Expired',
      dataIndex: 'expired',
      key: 'expired',
      sorter: (a: StatisticCreator, b: StatisticCreator) => a.expired - b.expired,
      defaultColumnChecked: true,
      render: (value: number) => formatNAText()(value),
    },
    {
      title: 'Refund',
      dataIndex: 'refund',
      key: 'refund',
      sorter: (a: StatisticCreator, b: StatisticCreator) => a.refund - b.refund,
      render: (value: number) => formatPrice()(value),
    },
  ], [])

  return columns
}
